import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Globe,
  Heart,
  Users,
  MessageSquare,
  Smartphone,
  Languages,
  Zap,
  Target,
  Lightbulb,
  Sparkles,
  Check,
  Shield,
  Clock,
  Star,
  ChevronRight,
} from 'lucide-react';
import { Button, Card, CardContent, LanguageModal } from '@/components/ui';
import { SignInButton, UserButton, useAuth } from '@clerk/clerk-react';
import { PageSeo } from '@/components/seo/PageSeo';

const content = {
  hr: {
    meta: {
      title: 'O nama | BookiApp',
      description: 'BookiApp je aplikacija za goste apartmana, vila i soba: WiFi, upute za dolazak, kućna pravila, lokalne preporuke i chat s automatskim prijevodom na 95+ jezika.',
    },
    title: 'O BookiApp-u',
    subtitle: 'Sve o boravku — u jednoj aplikaciji za vaše goste',
    badge: 'Naša priča',

    whatIs: {
      title: 'Što je BookiApp?',
      content: `BookiApp je aplikacija dizajnirana za iznajmljivače apartmana, vila i soba diljem svijeta. Gostu daje sve informacije o boravku na jednom mjestu — od WiFi lozinke i uputa za dolazak do lokalnih preporuka — kroz personaliziranu mobilnu aplikaciju (PWA) za smještaj koji je rezervirao.`,
      highlight: 'Ključna značajka je chat s automatskim prijevodom na 95+ jezika, što omogućuje nesmetanu komunikaciju između domaćina i gostiju bez obzira na jezične barijere.',
    },

    mission: {
      title: 'Naša misija',
      content: 'Demokratizirati premium gostoprimstvo. Želimo omogućiti svakom iznajmljivaču, od onoga s jednim apartmanom do velikih agencija, da pruži 5-zvjezdično iskustvo svojim gostima - bez potrebe za velikim budžetom ili tehničkim znanjem.',
      badge: 'Vizija',
    },

    problem: {
      title: 'Problem koji rješavamo',
      content: 'Svaki iznajmljivač zna koliko je frustrirajuće odgovarati na ista pitanja iznova i iznova.',
      items: [
        'Koja je WiFi lozinka?',
        'Gdje mogu parkirati?',
        'U koliko sati je check-out?',
        'Gdje je najbliži restoran?',
      ],
      note: 'A kada gost govori drugim jezikom, komunikacija postaje još teža.',
    },

    solution: {
      title: 'Naše rješenje',
      subtitle: 'Sve što trebate na jednom mjestu',
      items: [
        {
          icon: 'smartphone',
          title: 'Personalizirana aplikacija',
          desc: 'Mobilna web aplikacija koju gosti instaliraju jednim klikom. Sadrži sve informacije o boravku, radi offline i automatski se prevodi na jezik gosta.',
        },
        {
          icon: 'messages',
          title: 'Chat s prijevodom',
          desc: 'Real-time komunikacija gdje se svaka poruka automatski prevodi. Vi pišete hrvatski, gost čita na svom jeziku - i obrnuto.',
        },
        {
          icon: 'languages',
          title: '95+ jezika',
          desc: 'Podržavamo sve važne jezike, od njemačkog i talijanskog do japanskog i arapskog. Prijevod je instantan i besplatan.',
        },
        {
          icon: 'zap',
          title: 'Push obavijesti',
          desc: 'Obavijestite goste o važnim informacijama. Primite obavijest kad gost krene, stigne na parking ili uđe u smještaj.',
        },
      ],
    },

    forWho: {
      title: 'Za koga je BookiApp?',
      items: [
        { text: 'Vlasnike apartmana i vila koji žele smanjiti broj pitanja od gostiju', icon: 'home' },
        { text: 'Iznajmljivače koji rade s gostima iz različitih zemalja', icon: 'globe' },
        { text: 'Agencije koje upravljaju više objekata', icon: 'building' },
        { text: 'Bilo koga tko želi pružiti profesionalno iskustvo gostima', icon: 'star' },
      ],
    },

    comparison: {
      title: 'BookiApp vs alternativa',
      items: [
        {
          feature: 'Komunikacija',
          us: 'Dvosmjerna s prijevodom',
          them: 'Jednosmjerna ili nikakva',
        },
        {
          feature: 'Prijevod',
          us: '95+ jezika automatski',
          them: 'Ručno ili ograničeno',
        },
        {
          feature: 'Push obavijesti',
          us: 'Real-time za obje strane',
          them: 'Samo email ili ništa',
        },
        {
          feature: 'AI asistent',
          us: 'Uključen u sve planove',
          them: 'Nema ili dodatna naplata',
        },
      ],
      note: 'Nije zamjena za channel managere (Rentlio, Hostaway) - savršeno se nadopunjuje s njima. Oni upravljaju rezervacijama, mi poboljšavamo iskustvo gosta.',
    },

    team: {
      title: 'Tim',
      content: 'BookiApp razvija tim iz Hrvatske s iskustvom u turizmu i tehnologiji. Razumijemo izazove iznajmljivanja jer smo ih i sami iskusili.',
      values: [
        { icon: 'heart', title: 'Strast', desc: 'Volimo turizam i tehnologiju' },
        { icon: 'shield', title: 'Povjerenje', desc: 'Sigurnost vaših podataka' },
        { icon: 'clock', title: 'Podrška', desc: 'Brzi odgovori na sva pitanja' },
      ],
    },

    cta: {
      title: 'Spremni za promjenu?',
      subtitle: 'Pridružite se iznajmljivačima koji pružaju premium iskustvo gostima.',
      button: 'Pogledaj planove',
      features: ['Sve funkcionalnosti uključene', 'Bez skrivenih troškova', 'Sigurno plaćanje'],
    },
  },
  en: {
    meta: {
      title: 'About us | BookiApp',
      description: 'BookiApp is a guest app for apartments, villas and rooms: WiFi, arrival instructions, house rules, local tips and chat with automatic translation into 95+ languages.',
    },
    title: 'About BookiApp',
    subtitle: 'Guest Experience platform for modern hosts',
    badge: 'Our Story',

    whatIs: {
      title: 'What is BookiApp?',
      content: `BookiApp is a Guest Experience platform designed for apartment, villa and room hosts worldwide. It enables hosts to provide a professional guest experience through a mobile app (PWA) containing all stay information - from WiFi password to local recommendations.`,
      highlight: 'The key feature is chat with automatic translation to 95+ languages, enabling seamless communication between hosts and guests regardless of language barriers.',
    },

    mission: {
      title: 'Our Mission',
      content: 'To democratize premium hospitality. We want to enable every host, from those with a single apartment to large agencies, to provide a 5-star experience to their guests - without the need for a large budget or technical knowledge.',
      badge: 'Vision',
    },

    problem: {
      title: 'The Problem We Solve',
      content: 'Every host knows how frustrating it is to answer the same questions over and over.',
      items: [
        "What's the WiFi password?",
        'Where can I park?',
        'What time is check-out?',
        "Where's the nearest restaurant?",
      ],
      note: 'And when the guest speaks a different language, communication becomes even harder.',
    },

    solution: {
      title: 'Our Solution',
      subtitle: 'Everything you need in one place',
      items: [
        {
          icon: 'smartphone',
          title: 'Personalized Guest App',
          desc: "A mobile web app that guests install with one click. Contains all stay information, works offline and automatically translates to the guest's language.",
        },
        {
          icon: 'messages',
          title: 'Translated Chat',
          desc: 'Real-time communication where every message is automatically translated. You write in your language, guest reads in theirs - and vice versa.',
        },
        {
          icon: 'languages',
          title: '95+ Languages',
          desc: 'We support all major languages, from German and Italian to Japanese and Arabic. Translation is instant and free.',
        },
        {
          icon: 'zap',
          title: 'Push Notifications',
          desc: 'Notify guests about important information. Get notified when guest departs, arrives at parking or enters the accommodation.',
        },
      ],
    },

    forWho: {
      title: 'Who is BookiApp for?',
      items: [
        { text: 'Apartment and villa owners who want to reduce guest questions', icon: 'home' },
        { text: 'Hosts who work with guests from different countries', icon: 'globe' },
        { text: 'Agencies managing multiple properties', icon: 'building' },
        { text: 'Anyone who wants to provide a professional guest experience', icon: 'star' },
      ],
    },

    comparison: {
      title: 'BookiApp vs alternatives',
      items: [
        {
          feature: 'Communication',
          us: 'Two-way with translation',
          them: 'One-way or none',
        },
        {
          feature: 'Translation',
          us: '95+ languages automatic',
          them: 'Manual or limited',
        },
        {
          feature: 'Push notifications',
          us: 'Real-time for both sides',
          them: 'Email only or none',
        },
        {
          feature: 'AI assistant',
          us: 'Included in all plans',
          them: 'None or extra charge',
        },
      ],
      note: "It's not a replacement for channel managers (Rentlio, Hostaway) - it perfectly complements them. They manage bookings, we improve guest experience.",
    },

    team: {
      title: 'Team',
      content: "BookiApp is developed by a team from Croatia with experience in tourism and technology. We understand the challenges of hosting because we've experienced them ourselves.",
      values: [
        { icon: 'heart', title: 'Passion', desc: 'We love tourism and technology' },
        { icon: 'shield', title: 'Trust', desc: 'Your data security matters' },
        { icon: 'clock', title: 'Support', desc: 'Quick answers to all questions' },
      ],
    },

    cta: {
      title: 'Ready for a change?',
      subtitle: 'Join hosts who provide premium guest experiences.',
      button: 'View plans',
      features: ['All features included', 'No hidden costs', 'Secure payment'],
    },
  },
};

const solutionIcons = {
  smartphone: Smartphone,
  messages: MessageSquare,
  languages: Languages,
  zap: Zap,
};

const valueIcons = {
  heart: Heart,
  shield: Shield,
  clock: Clock,
};

export function AboutPage() {
  const { i18n } = useTranslation();
  const lang = (i18n.language === 'hr' ? 'hr' : 'en') as 'hr' | 'en';
  const t = content[lang];
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  const [langOpen, setLangOpen] = useState(false);
  const { isSignedIn } = useAuth();

  return (
    <div className="min-h-screen bg-gradient-to-b from-[hsl(var(--background))] via-[hsl(var(--background))] to-blue-50/30 dark:to-blue-950/10">
      <PageSeo path="/about" title={t.meta.title} description={t.meta.description} />
      {/* Decorative background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute top-40 right-20 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-40 left-1/3 w-80 h-80 bg-indigo-500/10 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative container mx-auto flex items-center justify-between p-4">
        <Link to="/" className="flex items-center gap-2 group">
          <img src="/favicon-original.png" alt="BookiApp" className="h-8 w-auto group-hover:scale-105 transition-transform" />
        </Link>
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => setLangOpen(true)} className="gap-2" aria-haspopup="dialog" aria-expanded={langOpen}>
            <Globe className="h-4 w-4" />
            {i18n.language.toUpperCase()}
          </Button>
          <Link to="/pricing">
            <Button className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 shadow-lg shadow-cyan-500/25">
              {lang === 'hr' ? 'Cijene' : 'Pricing'}
            </Button>
          </Link>
          {isSignedIn ? (
            <>
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="hover:bg-cyan-500/10">
                  Dashboard
                </Button>
              </Link>
              <UserButton
                afterSignOutUrl="/"
                appearance={{ elements: { avatarBox: 'h-9 w-9 rounded-xl ring-2 ring-transparent hover:ring-cyan-200 transition-all duration-200' } }}
              />
            </>
          ) : (
            <SignInButton mode="modal" forceRedirectUrl="/dashboard" signUpForceRedirectUrl="/dashboard">
              <Button variant="ghost" size="sm" className="hover:bg-cyan-500/10">
                {lang === 'hr' ? 'Prijava' : 'Sign In'}
              </Button>
            </SignInButton>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="relative container mx-auto px-4 py-16 md:py-24 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 text-sm font-medium mb-6">
          <Sparkles className="h-4 w-4" />
          {t.badge}
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-6">
          <span className="bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 bg-clip-text text-transparent">
            {t.title}
          </span>
        </h1>
        <p className="text-lg sm:text-xl text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto leading-relaxed">
          {t.subtitle}
        </p>
      </section>

      {/* What is BookiApp */}
      <section className="relative container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <Card className="overflow-hidden border-2 border-cyan-500/20 shadow-xl shadow-cyan-500/5">
            <div className="h-1 bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-500" />
            <CardContent className="p-8 md:p-12">
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 rounded-xl bg-gradient-to-br from-cyan-100 to-blue-100 dark:from-cyan-900/40 dark:to-blue-900/40">
                  <Lightbulb className="h-7 w-7 text-cyan-600 dark:text-cyan-400" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold">{t.whatIs.title}</h2>
              </div>
              <p className="text-lg text-[hsl(var(--muted-foreground))] leading-relaxed mb-6">
                {t.whatIs.content}
              </p>
              <div className="p-4 rounded-xl bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20">
                <p className="text-[hsl(var(--foreground))] font-medium flex items-start gap-3">
                  <Sparkles className="h-5 w-5 text-cyan-500 flex-shrink-0 mt-0.5" />
                  {t.whatIs.highlight}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Mission */}
      <section className="relative container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="relative rounded-3xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600" />
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIgMS44LTQgNC00czQgMS44IDQgNC0xLjggNC00IDQtNC0xLjgtNC00eiIvPjwvZz48L2c+PC9zdmc+')] opacity-30" />
            <div className="relative p-8 md:p-12 text-white">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm text-sm font-medium mb-4">
                <Target className="h-4 w-4" />
                {t.mission.badge}
              </div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4">{t.mission.title}</h2>
              <p className="text-lg opacity-90 leading-relaxed max-w-2xl">
                {t.mission.content}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Problem */}
      <section className="relative container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">{t.problem.title}</h2>
          <p className="text-lg text-[hsl(var(--muted-foreground))] mb-8">
            {t.problem.content}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {t.problem.items.map((item, i) => (
              <div
                key={i}
                className="p-4 rounded-xl bg-red-500/5 border border-red-500/20 hover:bg-red-500/10 transition-colors"
              >
                <p className="text-sm font-medium text-red-600 dark:text-red-400">"{item}"</p>
              </div>
            ))}
          </div>
          <p className="text-[hsl(var(--muted-foreground))] italic">
            {t.problem.note}
          </p>
        </div>
      </section>

      {/* Solution */}
      <section className="relative container mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 text-green-600 dark:text-green-400 text-sm font-medium mb-4">
            <Check className="h-4 w-4" />
            {t.solution.subtitle}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold">
            <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
              {t.solution.title}
            </span>
          </h2>
        </div>

        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-6">
          {t.solution.items.map((item, i) => {
            const Icon = solutionIcons[item.icon as keyof typeof solutionIcons];
            return (
              <Card
                key={i}
                className={`group relative overflow-hidden border-2 transition-all duration-500 ${
                  hoveredCard === i
                    ? 'border-cyan-500/50 shadow-2xl shadow-cyan-500/10 scale-[1.02]'
                    : 'border-[hsl(var(--border))] hover:border-cyan-500/30'
                }`}
                onMouseEnter={() => setHoveredCard(i)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                {/* Decorative gradient */}
                <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-cyan-500 to-blue-500 opacity-5 rounded-full -translate-y-16 translate-x-16 transition-transform duration-500 ${hoveredCard === i ? 'scale-150' : ''}`} />

                <CardContent className="relative p-6 md:p-8">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-100 to-blue-100 dark:from-cyan-900/40 dark:to-blue-900/40 flex items-center justify-center mb-5 transition-transform duration-300 ${hoveredCard === i ? 'scale-110 rotate-3' : ''}`}>
                    <Icon className="h-7 w-7 text-cyan-600 dark:text-cyan-400" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                  <p className="text-[hsl(var(--muted-foreground))] leading-relaxed">
                    {item.desc}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* For Who */}
      <section className="relative container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">{t.forWho.title}</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {t.forWho.items.map((item, i) => (
              <div
                key={i}
                className="flex items-center gap-4 p-5 rounded-xl bg-[hsl(var(--card))] border-2 border-[hsl(var(--border))] hover:border-cyan-500/30 hover:shadow-lg transition-all duration-300 group"
              >
                <div className="w-10 h-10 rounded-full bg-cyan-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-cyan-500/20 transition-colors">
                  <Users className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                </div>
                <span className="text-[hsl(var(--foreground))] font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="relative container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">{t.comparison.title}</h2>

          <Card className="overflow-hidden border-2 border-[hsl(var(--border))] shadow-xl">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[hsl(var(--muted))]/50">
                    <th className="text-left p-4 font-semibold">{lang === 'hr' ? 'Funkcija' : 'Feature'}</th>
                    <th className="text-center p-4 font-semibold text-cyan-600 dark:text-cyan-400">BookiApp</th>
                    <th className="text-center p-4 font-semibold text-[hsl(var(--muted-foreground))]">{lang === 'hr' ? 'Ostali' : 'Others'}</th>
                  </tr>
                </thead>
                <tbody>
                  {t.comparison.items.map((item, i) => (
                    <tr key={i} className="border-t border-[hsl(var(--border))]">
                      <td className="p-4 font-medium">{item.feature}</td>
                      <td className="p-4 text-center">
                        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/10 text-green-600 dark:text-green-400 text-sm font-medium">
                          <Check className="h-3 w-3" />
                          {item.us}
                        </span>
                      </td>
                      <td className="p-4 text-center text-[hsl(var(--muted-foreground))] text-sm">
                        {item.them}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <p className="text-center text-[hsl(var(--muted-foreground))] mt-6 text-sm italic">
            {t.comparison.note}
          </p>
        </div>
      </section>

      {/* Team */}
      <section className="relative container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/10 text-red-600 dark:text-red-400 text-sm font-medium mb-4">
              <Heart className="h-4 w-4" />
              {lang === 'hr' ? 'Naš tim' : 'Our team'}
            </div>
            <h2 className="text-2xl md:text-3xl font-bold mb-4">{t.team.title}</h2>
            <p className="text-lg text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto">
              {t.team.content}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {t.team.values.map((value, i) => {
              const Icon = valueIcons[value.icon as keyof typeof valueIcons];
              return (
                <Card key={i} className="text-center border-2 border-[hsl(var(--border))] hover:border-red-500/30 transition-colors">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
                      <Icon className="h-6 w-6 text-red-500" />
                    </div>
                    <h3 className="font-bold mb-2">{value.title}</h3>
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">{value.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container mx-auto px-4 py-16 md:py-20">
        <div className="max-w-4xl mx-auto">
          <div className="relative rounded-3xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600" />
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIgMS44LTQgNC00czQgMS44IDQgNC0xLjggNC00IDQtNC0xLjgtNC00eiIvPjwvZz48L2c+PC9zdmc+')] opacity-30" />

            <div className="relative p-8 md:p-12 lg:p-16 text-center text-white">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-sm font-medium mb-6">
                <Star className="h-4 w-4" />
                {lang === 'hr' ? 'Pridružite se' : 'Join us'}
              </div>

              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
                {t.cta.title}
              </h2>
              <p className="text-lg md:text-xl opacity-90 mb-8 max-w-xl mx-auto">
                {t.cta.subtitle}
              </p>

              <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
                <Link to="/pricing">
                  <Button
                    size="lg"
                    className="bg-white text-blue-600 hover:bg-white/90 shadow-xl shadow-black/20 text-base md:text-lg px-8 group"
                  >
                    {t.cta.button}
                    <ChevronRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-2 border-white/30 bg-transparent text-white hover:bg-white/10 text-base md:text-lg px-8"
                  >
                    {lang === 'hr' ? 'Kontaktirajte nas' : 'Contact us'}
                  </Button>
                </Link>
              </div>

              <div className="flex flex-wrap justify-center gap-6 text-sm opacity-80">
                {t.cta.features.map((feature, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Check className="h-4 w-4" />
                    {feature}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t py-8">
        <div className="container mx-auto px-4 text-center text-sm text-[hsl(var(--muted-foreground))]">
          <p>&copy; 2026 BookiApp. {lang === 'hr' ? 'Sva prava pridržana.' : 'All rights reserved.'}</p>
        </div>
      </footer>

      <LanguageModal open={langOpen} onClose={() => setLangOpen(false)} scope="marketing" />
    </div>
  );
}

export default AboutPage;
